import { ApiClient, apiClient } from '../apiClient';

// ===============================
// TIPOS DE REFERENCIAS
// ===============================

export interface Faena {
  id: number;
  codigo: string;
  descripcion: string;
  ubicacion?: string;
  estado: 'activa' | 'pausada' | 'finalizada'; // 🔧 CORREGIDO: enum correcto para faenas
}

export interface Predio {
  id: number;
  codigo: string;
  descripcion: string;
  superficie_ha?: number;
  propietario?: string;
  estado: 'activo' | 'inactivo';
}

export interface Rodal {
  id: number;
  codigo: string;
  descripcion: string;
  predio_id: number;
  especie?: string;
  año_plantacion?: number;
  superficie_ha?: number;
  estado: 'activo' | 'inactivo';
}

export interface Insumo {
  id: number;
  codigo: string;
  descripcion: string;
  unidad_medida: string;
  categoria?: string;
  precio_unitario?: number;
  estado: 'activo' | 'inactivo';
}

export interface Maquina {
  id: number;
  codigo: string;
  tipo: string;
  marca: string;
  modelo: string;
  descripcion?: string;
  estado: 'activo' | 'inactivo';
}

// ===============================
// SERVICIO DE REFERENCIAS
// ===============================

export interface FaenaCalificadorCatalog {
  id: number;
  codigo: string;
  descripcion: string;
  opciones: string[];
  activo: boolean;
  orden: number;
}

export class ReferenciasService {

  // 🏷️ CALIFICADORES DE FAENA
  static async obtenerCalificadores(): Promise<FaenaCalificadorCatalog[]> {
    try {
      const response = await apiClient.get('/faenas/calificadores');
      const data = response.data || response || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error al obtener calificadores:', error);
      return [];
    }
  }

  // 📋 FAENAS
  static async obtenerFaenas(): Promise<Faena[]> {
    try {

      const response = await apiClient.get('/faenas/lista');

      // La API devuelve directamente el array, no envuelto en .data
      const faenas = response.data || response || [];

      return Array.isArray(faenas) ? faenas : [];
    } catch (error) {
      console.error('Error al obtener faenas:', error);
      throw new Error('Error al cargar faenas');
    }
  }

  // 🏞️ PREDIOS
  static async obtenerPredios(): Promise<Predio[]> {
    try {

      const response = await apiClient.get('/predios/lista');

      // La API devuelve directamente el array, no envuelto en .data
      const predios = response.data || response || [];

      return Array.isArray(predios) ? predios : [];
    } catch (error) {
      console.error('Error al obtener predios:', error);
      throw new Error('Error al cargar predios');
    }
  }

  // 🌳 RODALES (filtrados por predio)
  static async obtenerRodales(predioId?: number): Promise<Rodal[]> {
    try {
      const url = predioId ? `/rodales/lista?predio_id=${predioId}` : '/rodales/lista';

      const response = await apiClient.get(url);

      // La API devuelve directamente el array, no envuelto en .data
      const rodales = response.data || response || [];

      return Array.isArray(rodales) ? rodales : [];
    } catch (error) {
      console.error('Error al obtener rodales:', error);
      throw new Error('Error al cargar rodales');
    }
  }

  // 📦 INSUMOS
  static async obtenerInsumos(): Promise<Insumo[]> {
    try {
      const response = await ApiClient.get<any>('/insumos');
      return (response.data ?? response) || [];
    } catch (error) {
      console.error('Error al obtener insumos:', error);
      throw new Error('Error al cargar insumos');
    }
  }

  // 🚜 MÁQUINAS
  static async obtenerMaquinas(): Promise<Maquina[]> {
    try {
      const response = await ApiClient.get<any>('/maquinas');
      return (response.data ?? response) || [];
    } catch (error) {
      console.error('Error al obtener máquinas:', error);
      throw new Error('Error al cargar máquinas');
    }
  }

  // ===============================
  // HELPERS PARA FORMULARIOS
  // ===============================

  /**
   * Convertir referencias a opciones para selects
   */
  static toSelectOptions<T extends { id: number; codigo: string; descripcion: string }>(
    items: T[]
  ): Array<{ value: number; label: string; disabled?: boolean }> {
    return items
      .filter(item => {
        // 🔧 CORREGIDO: Filtrar por estados activos (activo/activa)
        const estado = (item as any).estado;
        return estado === 'activo' || estado === 'activa';
      })
      .map(item => ({
        value: item.id,
        label: `${item.codigo} - ${item.descripcion}`,
        disabled: false // Ya filtrados por activos
      }));
  }

  /**
   * Buscar referencia por ID
   */
  static buscarPorId<T extends { id: number }>(items: T[], id: number): T | undefined {
    return items.find(item => item.id === id);
  }

  /**
   * Filtrar activos únicamente
   */
  static filtrarActivos<T extends { estado: string }>(items: T[]): T[] {

    // 🔧 CORREGIDO: Manejar diferentes tipos de estado
    const filtered = items.filter(item => {
      // Para faenas: 'activa' es el estado activo
      // Para predios/rodales: 'activo' es el estado activo
      const isActive = item.estado === 'activo' || item.estado === 'activa';

      return isActive;
    });

    return filtered;
  }
}

export default ReferenciasService;